/**
* 运营分析页筛选条件组件 - 提供内容搜索和日期范围两个筛选表单项
*
* @author By AICode
* @date 2026-03-18
*/
<template>
  <div>
    <div class="operations_form">
      <div class="date_range">
        <span class="date_label">申请时间</span>
        <a-range-picker :show-time="{ format: 'HH:mm' }" format="YYYY-MM-DD HH:mm" :disabled-date="disabledDate"
          :value="applyRangeValue" :placeholder="['开始时间', '结束时间']" separator="→" style="width: 290px;"
          @change="chooseApplyDateRange" @ok="dateRangeOk" :getCalendarContainer="getCalendarContainer()">
          <a-icon slot="suffixIcon" type="calendar" />
        </a-range-picker>
      </div>
      <div class="date_range">
        <span class="date_label">生效时间</span>
        <a-range-picker :show-time="{ format: 'HH:mm' }" format="YYYY-MM-DD HH:mm" :disabled-date="disabledDate"
          :value="activeRangeValue" :placeholder="['开始时间', '结束时间']" separator="→" style="width: 290px;"
          @change="chooseActiveDateRange" @ok="dateRangeOk" :getCalendarContainer="getCalendarContainer()">
          <a-icon slot="suffixIcon" type="calendar" />
        </a-range-picker>
      </div>

      <div class="content">
        <div class="input_icon_box">
          <div id="ops_title" name="ops_title" tabindex="0" hidefocus="true" class="content_input input"
            @click.stop="clickContent" @blur="blurDiv">
            <div class="each_content_box">
              <span class="placeholder_text" v-show="isClickInpFlag">搜索License ID、项目名称、申请工单号、申请人、客户名称</span>
              <div class="each_content_wrap" v-if="showSearchFlag">
                <div class="each_content" v-for="(item, index) in searchContentCombindList" :key="item.combindedBlock">
                  <a-tooltip :title="item.combindedBlock">
                    <span class="each_text">{{ item.combindedBlock }}</span>
                  </a-tooltip>
                  <span class="delete_icon" @click="removeInCombind(item, index)"></span>
                </div>
              </div>
              <div class="search_content" v-if="!showSearchFlag && searchContentCombindList.length">
                <a-tooltip :title="setSearchText()">
                  <span class="search_text">{{ setSearchText() }}</span>
                </a-tooltip>
              </div>
            </div>
          </div>
          <div class="search_icon" @click="searchByContent"></div>
          <div :style="{ top: modelTop }" class="multi_function_search_box" v-if="showSearchFlag"
            v-clickoutside="handleClickOutside">
            <ManageSearchOverlay @transferCombinedContent="getCombinedData"></ManageSearchOverlay>
          </div>
        </div>
      </div>

      <a-space>
        <a-tooltip :title="!hasChanged ? '筛选条件、自定义表头、排序未发生变化，不可操作' : ''">
          <span :class="['btn_action_wrapper', { 'btn_action_wrapper--disabled': !hasChanged }]">
            <a-button class="btn_save" :disabled="!hasChanged" :style="!hasChanged ? 'pointer-events: none;' : ''"
              @click="$emit('save')" type="primary">保存</a-button>
          </span>
        </a-tooltip>
        <!-- sheetLimitReached 优先提示页签上限，其次提示未变更 - By AICode -->
        <a-tooltip :title="saveAsTooltip">
          <span :class="['btn_action_wrapper', { 'btn_action_wrapper--disabled': !hasChanged || sheetLimitReached }]">
            <a-button class="btn_save_as" :disabled="!hasChanged || sheetLimitReached"
              :style="(!hasChanged || sheetLimitReached) ? 'pointer-events: none;' : ''"
              @click="addDashboardVisible = true" type="primary">另存为</a-button>
          </span>
        </a-tooltip>
        <CustomTableHeader :data="tableHeaderData" :default-value="tableHeaderDefaultValue"
          :recovery-value="tableHeaderRecoveryValue" :require-each-group="true" @save="onTableHeaderSave" />
      </a-space>

    </div>

    <AddDashboard :visible="addDashboardVisible" @ok="handleAddDashboardOk" @cancel="addDashboardVisible = false" />
  </div>
</template>

<script>
import { Select, DatePicker, Icon, Button, Space, Tooltip } from 'ant-design-vue'
import moment from 'moment'
import './filterCondition.less'
import ManageSearchOverlay from './components/ManageSearchOverlay/ManageSearchOverlay.vue'
import CustomTableHeader from '@/components/customTableHeader/CustomTableHeader.vue'
import clickoutside from '@/directives/clickOutside.js'
import AddDashboard from '../AddDashboard.vue'

export default {
  name: 'OperationsFilterCondition',
  components: {
    ManageSearchOverlay,
    CustomTableHeader,
    AddDashboard,
    'a-select': Select,
    'a-select-option': Select.Option,
    'a-range-picker': DatePicker.RangePicker,
    'a-icon': Icon,
    'a-button': Button,
    'a-space': Space,
    'a-tooltip': Tooltip,
  },
  directives: {
    clickoutside,
  },
  props: {
    tableHeaderData: { type: Array, default: () => [] },
    tableHeaderDefaultValue: { type: Array, default: () => [] },
    tableHeaderRecoveryValue: { type: Array, default: () => [] },
    filterParams: { type: Object, default: () => ({}) },
    hasChanged: { type: Boolean, default: false },
    sheetLimitReached: { type: Boolean, default: false },
  },
  data() {
    return {
      addDashboardVisible: false,
      modelTop: '40px',
      showSearchFlag: false,
      isClickInpFlag: true,
      searchContentCombindList: [],
      isSyncingSearchContent: false,
    }
  },
  computed: {
    applyRangeValue() {
      const from = this.filterParams.applyTimeFrom
      const to = this.filterParams.applyTimeTo
      return from && to ? [moment(from, 'YYYY-MM-DD HH:mm'), moment(to, 'YYYY-MM-DD HH:mm')] : []
    },
    activeRangeValue() {
      const from = this.filterParams.activeTimeFrom
      const to = this.filterParams.activeTimeTo
      return from && to ? [moment(from, 'YYYY-MM-DD HH:mm'), moment(to, 'YYYY-MM-DD HH:mm')] : []
    },
    // 另存为按钮 tooltip，页签数量已达上限时优先提示 - By AICode
    saveAsTooltip() {
      if (this.sheetLimitReached) {
        return '运营分析页签不能超过20'
      }
      if (!this.hasChanged) {
        return '筛选条件、自定义表头、排序未发生变化，不可操作'
      }
      return ''
    },
  },
  methods: {
    // 拼接搜索标签展示文本 - By AICode
    setSearchText() {
      if (this.searchContentCombindList.length) {
        return this.searchContentCombindList.map(item => item.combindedBlock).join('，')
      }
      return ''
    },

    getCalendarContainer() {
      return triggerNode => triggerNode.parentNode
    },

    // 禁用 2001 年以前及今天之后的日期 - By AICode
    disabledDate(current) {
      return current < moment('2001') || current > moment().endOf('day')
    },

    // 点击内容输入框展开搜索面板 - By AICode
    clickContent() {
      this.showSearchFlag = true
    },

    blurDiv() {
      if (!this.searchContentCombindList.length) {
        this.isClickInpFlag = true
      }
    },

    handleClickOutside() {
      this.showSearchFlag = false
    },

    // 接收 SelectSearch 组件选中的搜索组合块 - By AICode
    getCombinedData(choosedCombindArr) {
      if (this.searchContentCombindList.length < 5) {
        if (this.searchContentCombindList.some(item => item.combindedBlock === choosedCombindArr.combindedBlock)) {
          return
        }
        this.searchContentCombindList.push(choosedCombindArr)
      } else {
        this.$message.warning('仅可选择6项', 1)
      }
      if (this.searchContentCombindList.length) {
        this.isClickInpFlag = false
      }
    },

    // 将 filters 字段映射为搜索标签所需结构，保证已保存条件可回显 - By AICode
    syncSearchContentFromFilters(filters = {}) {
      const fieldLabelMap = {
        licenseId: 'License ID',
        projectName: '项目名称',
        orderNo: '申请工单号',
        applicantName: '申请人',
        customerName: '最终客户',
        businessId: '商机 ID',
        envType: 'License类型',
      }
      const envTypeValueLabel = {
        '1': '测试',
        '2': '生产',
        '3': 'POC',
        '4': '正式',
        '5': '续约',
        '6': '功能验证',
      }
      const nextList = []
      Object.keys(filters || {}).forEach((fieldKey) => {
        const rawValue = filters[fieldKey]
        const values = Array.isArray(rawValue)
          ? rawValue
          : String(rawValue || '').split(',').filter(item => item !== '')
        values.forEach((value) => {
          const valueText = String(value || '').trim()
          if (!valueText) {
            return
          }
          const displayText = fieldKey === 'envType' && envTypeValueLabel[valueText]
            ? envTypeValueLabel[valueText]
            : valueText
          nextList.push({
            combindedBlock: `${fieldLabelMap[fieldKey] || fieldKey}:${displayText}`,
            params: fieldKey,
            paramsValue: valueText,
          })
        })
      })
      const dedupedList = []
      const seen = new Set()
      nextList.forEach((item) => {
        if (!seen.has(item.combindedBlock)) {
          seen.add(item.combindedBlock)
          dedupedList.push(item)
        }
      })
      const currentKey = this.searchContentCombindList.map(item => item.combindedBlock).join('|')
      const nextKey = dedupedList.map(item => item.combindedBlock).join('|')
      if (currentKey === nextKey) {
        return
      }
      this.isSyncingSearchContent = true
      this.searchContentCombindList = dedupedList
      this.isClickInpFlag = !dedupedList.length
      this.$nextTick(() => {
        this.isSyncingSearchContent = false
      })
    },

    // 移除搜索标签并触发筛选更新 - By AICode
    removeInCombind(item, index) {
      this.searchContentCombindList.splice(index, 1)
      this.$emit('filter-change', this.buildFilterParams())
    },

    // 点击搜索图标触发查询 - By AICode
    searchByContent() {
      this.$emit('filter-change', this.buildFilterParams())
    },

    // 申请时间范围变更后同步参数状态 - By AICode
    chooseApplyDateRange(date, dateString) {
      this.updateDateRange('apply', dateString)
    },

    // 生效时间范围变更后同步参数状态 - By AICode
    chooseActiveDateRange(date, dateString) {
      this.updateDateRange('active', dateString)
    },

    // 日期范围清空时立即触发查询，避免保留历史时间条件 - By AICode
    updateDateRange(rangeType, dateString) {
      const fromKey = rangeType === 'apply' ? 'applyTimeFrom' : 'activeTimeFrom'
      const toKey = rangeType === 'apply' ? 'applyTimeTo' : 'activeTimeTo'
      const nextRange = {}
      if (dateString[0] === '' && dateString[1] === '') {
        nextRange[fromKey] = ''
        nextRange[toKey] = ''
        this.$emit('time-range-change', nextRange)
        this.$emit('filter-change', this.buildFilterParams(nextRange))
      } else {
        nextRange[fromKey] = dateString[0]
        nextRange[toKey] = dateString[1]
        this.$emit('time-range-change', nextRange)
      }
    },

    // 日期范围确认后触发查询 - By AICode
    dateRangeOk() {
      this.$emit('filter-change', this.buildFilterParams())
    },

    // 自定义表头保存回调
    onTableHeaderSave(keys) {
      // console.log('keys', keys)
      this.$emit('table-header-change', keys)
    },

    // 另存为看板确认回调 - By AICode
    handleAddDashboardOk(values) {
      this.$emit('save-as', values)
      this.addDashboardVisible = false
    },

    // 将当前筛选状态组装为统一的参数对象 - By AICode
    buildFilterParams(extraParams = {}) {
      const params = {
        applyTimeFrom: this.filterParams.applyTimeFrom || '',
        applyTimeTo: this.filterParams.applyTimeTo || '',
        activeTimeFrom: this.filterParams.activeTimeFrom || '',
        activeTimeTo: this.filterParams.activeTimeTo || '',
        ...extraParams,
      }
      this.searchContentCombindList.forEach(item => {
        params[item.params] = (params[item.params] ? params[item.params] + ',' : '') + item.paramsValue
      })
      return params
    },
  },
  watch: {
    // 响应父组件 filters 变化进行回显，避免切换看板后丢失已保存筛选项 - By AICode
    'filterParams.filters': {
      immediate: true,
      deep: true,
      handler(newValue) {
        this.syncSearchContentFromFilters(newValue || {})
      },
    },
    searchContentCombindList(newValue) {
      if (newValue.length < 1) {
        this.isClickInpFlag = true
      }
      if (newValue.length < 3) {
        this.modelTop = '40px'
      } else if (newValue.length < 5) {
        this.modelTop = '72px'
      } else {
        this.modelTop = '104px'
      }
      if (this.isSyncingSearchContent) {
        return
      }
      this.$emit('filter-change', this.buildFilterParams())
    },
  },
}
</script>

<style lang="less" scoped>
.btn_action_wrapper {
  display: inline-block;

  &--disabled {
    cursor: not-allowed;
  }
}

.search_text {
  vertical-align: middle;
  display: inline-block;
  max-width: 100%;
  background: #e6f4ff;
  font-size: 12px;
  line-height: 17px;
  font-weight: 400;
  color: #4e4e4e;
  text-overflow: ellipsis;
  overflow: hidden;
  word-break: normal;
  white-space: nowrap;
}
</style>
